import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { easeOutCubic } from '../../utils';
import { CrossIcon } from '../Icons';
import { Text } from '../Text';

export const SidebarTitle = ({
  children,
  handleClose,
}: {
  children: React.ReactNode;
  handleClose?: () => void;
}) => (
  <div className="flex flex-col top-0 sticky bg-[#FFFFFF] border-b border-b-[#F2F2F2]">
    <div className="flex items-center justify-between py-3 px-6 bg-[#F5F5F7] text-[#707075]">
      <div className="flex items-center gap-3">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="22"
          height="18"
          viewBox="0 0 22 18"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M17.2 1.5H4.8C3.93517 1.5 3.37676 1.50117 2.95175 1.53589C2.54426 1.56918 2.39318 1.6257 2.31901 1.66349C2.03677 1.8073 1.8073 2.03677 1.66349 2.31901C1.6257 2.39318 1.56918 2.54426 1.53589 2.95175C1.50117 3.37676 1.5 3.93517 1.5 4.8V13.2C1.5 14.0648 1.50117 14.6232 1.53589 15.0482C1.56918 15.4557 1.6257 15.6068 1.66349 15.681C1.8073 15.9632 2.03677 16.1927 2.31901 16.3365C2.39318 16.3743 2.54426 16.4308 2.95175 16.4641C3.37676 16.4988 3.93517 16.5 4.8 16.5H17.2C18.0648 16.5 18.6232 16.4988 19.0482 16.4641C19.4557 16.4308 19.6068 16.3743 19.681 16.3365C19.9632 16.1927 20.1927 15.9632 20.3365 15.681C20.3743 15.6068 20.4308 15.4557 20.4641 15.0482C20.4988 14.6232 20.5 14.0648 20.5 13.2V4.8C20.5 3.93517 20.4988 3.37676 20.4641 2.95175C20.4308 2.54426 20.3743 2.39318 20.3365 2.31901C20.1927 2.03677 19.9632 1.8073 19.681 1.66349C19.6068 1.6257 19.4557 1.56918 19.0482 1.53589C18.6232 1.50117 18.0648 1.5 17.2 1.5ZM0.32698 1.63803C0 2.27976 0 3.11984 0 4.8V13.2C0 14.8802 0 15.7202 0.32698 16.362C0.614601 16.9265 1.07354 17.3854 1.63803 17.673C2.27976 18 3.11984 18 4.8 18H17.2C18.8802 18 19.7202 18 20.362 17.673C20.9265 17.3854 21.3854 16.9265 21.673 16.362C22 15.7202 22 14.8802 22 13.2V4.8C22 3.11984 22 2.27976 21.673 1.63803C21.3854 1.07354 20.9265 0.614601 20.362 0.32698C19.7202 0 18.8802 0 17.2 0H4.8C3.11984 0 2.27976 0 1.63803 0.32698C1.07354 0.614601 0.614601 1.07354 0.32698 1.63803Z"
            fill="#707075"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M2.83826 6.14705C3.03319 5.78157 3.48748 5.64331 3.85297 5.83823L11 9.65L18.1471 5.83823C18.5126 5.64331 18.9669 5.78157 19.1618 6.14705C19.3567 6.51254 19.2185 6.96684 18.853 7.16176L11.353 11.1618C11.1324 11.2794 10.8677 11.2794 10.6471 11.1618L3.14708 7.16176C2.7816 6.96684 2.64334 6.51254 2.83826 6.14705Z"
            fill="#707075"
          />
        </svg>
        <Text variant="H3">{children}</Text>
      </div>
      <div onClick={handleClose} className="cursor-pointer">
        <CrossIcon />
      </div>
    </div>
  </div>
);

export const SidebarContent = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-col gap-6 h-full overflow-y-scroll py-0 px-6">
    {children}
  </div>
);

export const SidebarFooter = ({ children }: { children: React.ReactNode }) => (
  <div className="h-[84px] sticky flex items-center justify-between bottom-0 w-full left-0 bg-white pt-3 px-6 border-t border-t-[#F2F2F2]">
    {children}
  </div>
);

export const Sidebar = ({
  children,
  isOpen,
  handleClose,
}: {
  isOpen: boolean;
  handleClose: () => void;
  children: React.ReactNode;
}) => {
  const animationVariants = {
    initial: { x: '100%', opacity: 0 },
    animate: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.3, ease: easeOutCubic },
    },
    exit: {
      x: '100%',
      opacity: 0,
      transition: { duration: 0.3, ease: easeOutCubic },
    },
  };

  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      // Inject handleClose only into SidebarTitle, if needed
      if (child.type === SidebarTitle) {
        return React.cloneElement(
          // TODO: refactor without type assertion, or add comment
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          child as React.ReactElement<{ handleClose: () => void }>,
          { handleClose },
        );
      }
      return child;
    }
    return null;
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed top-0 right-0 bg-white h-screen w-[480px] border-l border-[#EEE] z-40"
            variants={animationVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <div className="flex flex-col gap-2 h-full">
              {childrenWithProps}
            </div>
          </motion.div>
          <div
            className="top-0 w-full left-0 h-full fixed z-[39]"
            style={{
              backgroundColor: 'rgba(0,0,0, .5)',
            }}
            onClick={handleClose}
          />
        </>
      )}
    </AnimatePresence>
  );
};
